import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Mail, MapPin, Phone, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { SITE_CONFIG } from "@/lib/constants";
import { SERVICES, servicePath, STATIC_PATHS, type Lang, type ServiceDef } from "@/lib/services";
import { ui } from "@/lib/siteText";
import { useActiveServiceKeys } from "@/hooks/useServicePlaces";

const telHref = `tel:${SITE_CONFIG.phone.replace(/\s/g, "")}`;

export function Kicker({ children }: { children: React.ReactNode }) {
  return <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary">{children}</p>;
}

export function Crumbs({ items }: { items: { name: string; to?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3" />}
          {it.to ? <Link to={it.to} className="hover:text-primary">{it.name}</Link> : <span className="text-foreground">{it.name}</span>}
        </span>
      ))}
    </nav>
  );
}

export function PhpAlert({ lang }: { lang: Lang }) {
  const t = ui(lang);
  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-5 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-3">
          <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <p className="font-display text-lg font-semibold">{t.alertTitle}</p>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t.alertText}</p>
          </div>
        </div>
        <Button asChild variant="destructive" className="shrink-0">
          <Link to={servicePath("cyber-scan", lang)}>{t.alertCta}</Link>
        </Button>
      </div>
    </div>
  );
}

export function ServiceGrid({ lang, exclude, segments = [], compact }: { lang: Lang; exclude?: string; segments?: string[]; compact?: boolean }) {
  const { data: active } = useActiveServiceKeys();
  const list = SERVICES.filter((s) => s.key !== exclude && (!active || active.has(s.key)));
  return (
    <div className={compact ? "grid gap-4 sm:grid-cols-2" : "grid gap-4 sm:grid-cols-2 lg:grid-cols-4"}>
      {list.map((s) => (
        <ServiceCard key={s.key} s={s} lang={lang} to={servicePath(s.key, lang, ...segments)} />
      ))}
    </div>
  );
}

export function ServiceCard({ s, lang, to }: { s: ServiceDef; lang: Lang; to: string }) {
  const Icon = s.icon;
  return (
    <Link to={to} className="group flex flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary">
      <Icon className="h-6 w-6 text-primary" />
      <h3 className="mt-4 font-display text-lg font-semibold leading-tight">{s.name[lang]}</h3>
      <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.short[lang]}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
        {ui(lang).moreInfo} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

export function FaqBlock({ lang, items }: { lang: Lang; items: { q: string; a: string }[] }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">{ui(lang).faq}</h2>
      <Accordion type="single" collapsible className="mt-4">
        {items.map((f, i) => (
          <AccordionItem key={i} value={`f${i}`}>
            <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export function ContactDetails({ lang }: { lang: Lang }) {
  const t = ui(lang);
  const a = SITE_CONFIG.address;
  const rows = [
    { icon: Phone, label: t.callUs, value: SITE_CONFIG.phone, href: telHref },
    { icon: Mail, label: t.mailUs, value: SITE_CONFIG.email, href: `mailto:${SITE_CONFIG.email}` },
    { icon: MapPin, label: t.visitUs, value: `${a.street}, ${a.postalCode} ${a.city}`, href: `https://maps.google.com/?q=${encodeURIComponent(`${a.street} ${a.postalCode} ${a.city}`)}` },
  ];
  return (
    <ul className="space-y-4">
      {rows.map((r) => (
        <li key={r.label}>
          <a href={r.href} className="flex items-start gap-3 hover:text-primary">
            <r.icon className="mt-0.5 h-5 w-5 text-primary" />
            <span>
              <span className="block font-mono text-xs uppercase tracking-wider text-muted-foreground">{r.label}</span>
              <span className="font-medium">{r.value}</span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

export function ContactSection({ lang, subject }: { lang: Lang; subject: string }) {
  const t = ui(lang);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get("name") || "").trim().slice(0, 100);
    const email = String(fd.get("email") || "").trim().slice(0, 255);
    const phone = String(fd.get("phone") || "").trim().slice(0, 30) || null;
    const message = String(fd.get("message") || "").trim().slice(0, 2000);
    if (!name || !email || !message) return;
    setState("sending");
    const { error } = await supabase.from("contact_messages").insert({ name, email, phone, subject: subject.slice(0, 200), message });
    setState(error ? "error" : "sent");
  };

  return (
    <section id="contact" className="border-t border-border bg-card/50">
      <div className="container grid gap-10 py-16 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <h2 className="font-display text-3xl font-semibold">{t.formTitle}</h2>
          <p className="mt-3 text-muted-foreground">{t.formText}</p>
          <div className="mt-8">
            <ContactDetails lang={lang} />
          </div>
        </div>
        {state === "sent" ? (
          <div className="flex items-center rounded-lg border border-primary/50 bg-primary/10 p-8 font-medium">{t.sent}</div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-4 rounded-lg border border-border bg-background p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input name="name" placeholder={t.name} required maxLength={100} aria-label={t.name} />
              <Input name="phone" placeholder={t.phone} maxLength={30} aria-label={t.phone} />
            </div>
            <Input name="email" type="email" placeholder={t.email} required maxLength={255} aria-label={t.email} />
            <Textarea name="message" placeholder={t.message} required maxLength={2000} rows={5} aria-label={t.message} />
            {state === "error" && <p className="text-sm text-destructive">{t.sendError}</p>}
            <Button type="submit" size="lg" disabled={state === "sending"}>
              {state === "sending" ? t.sending : t.send}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

const NL_TEXT: Record<Lang, (p?: string) => string> = {
  nl: (p) => (p ? `Meer over Cyscom in ${p} en de rest van Nederland op onze Nederlandse website.` : "Meer over Cyscom op onze Nederlandse website."),
  en: (p) => (p ? `More about Cyscom in ${p} on our Dutch website.` : "More about Cyscom on our Dutch website."),
  fr: (p) => (p ? `En savoir plus sur Cyscom à ${p} sur notre site néerlandais.` : "En savoir plus sur Cyscom sur notre site néerlandais."),
};

export function NlSiteLink({ lang, placeName }: { lang: Lang; placeName?: string }) {
  return (
    <section className="border-t border-border py-10">
      <div className="container flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <p className="text-muted-foreground">{NL_TEXT[lang](placeName)}</p>
        <Button asChild variant="outline">
          <a href="https://cyscom.nl">Naar cyscom.nl</a>
        </Button>
      </div>
    </section>
  );
}

/** Hides a service's pages when it is switched off in /admin. */
export function ServiceGate({ serviceKey, children, fallback }: { serviceKey: string; children: React.ReactNode; fallback: React.ReactNode }) {
  const { data: active } = useActiveServiceKeys();
  if (active && !active.has(serviceKey)) return <>{fallback}</>;
  return <>{children}</>;
}

export { telHref, STATIC_PATHS };
